import { Link, useParams, Navigate } from "react-router-dom";
import { useLabPanel, panelStatusBadge } from "@/hooks/useLabPanels";
import { AppLayout } from "@/components/Layout";
import { Card } from "@/components/Card";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { LoadingScreen } from "@/components/LoadingScreen";
import { Alert } from "@/components/Alert";
import { Button } from "@/components/Button";
import { LabResultRow } from "@/components/LabResultRow";

export default function LabDetail() {
  const { panelId } = useParams<{ panelId: string }>();
  const panelQ = useLabPanel(panelId);

  if (panelQ.isLoading) return <LoadingScreen />;

  if (panelQ.error) {
    return (
      <AppLayout>
        <PageHeader title="Lab Results" />
        <Alert tone="error">{(panelQ.error as Error).message}</Alert>
        <div className="mt-4">
          <Link to="/labs" className="text-fh-green hover:text-fh-green-dark font-semibold">
            ← Back to all labs
          </Link>
        </div>
      </AppLayout>
    );
  }

  const panel = panelQ.data;
  if (!panel) return <Navigate to="/labs" replace />;

  const status = panelStatusBadge(panel.status);
  const dateShown = panel.received_at ?? panel.collected_at ?? panel.ordered_at;
  const dateLabel = panel.received_at
    ? "Results received"
    : panel.collected_at
    ? "Sample collected"
    : "Ordered";

  return (
    <AppLayout>
      <div className="mb-2">
        <Link to="/labs" className="text-[13px] text-fh-green hover:text-fh-green-dark font-semibold">
          ← Back to all labs
        </Link>
      </div>

      <PageHeader
        title={panel.panel_name}
        subtitle={`${dateLabel} ${formatDate(dateShown)}${panel.lab_provider ? ` · ${panel.lab_provider}` : ""}`}
        right={
          panel.pdf_url ? (
            <a href={panel.pdf_url} target="_blank" rel="noreferrer">
              <Button size="md" variant="secondary">Download PDF</Button>
            </a>
          ) : undefined
        }
      />

      <div className="flex items-center gap-3 mb-6">
        <StatusBadge tone={status.tone} label={status.label} />
        {panel.out_of_range_count > 0 ? (
          <span className="text-[12.5px] text-amber-700 font-semibold">
            {panel.out_of_range_count} {panel.out_of_range_count === 1 ? "value" : "values"} outside reference range
          </span>
        ) : panel.lab_results.length > 0 ? (
          <span className="text-[12.5px] text-fh-green-dark font-semibold">All values within reference range</span>
        ) : null}
      </div>

      {/* Clinician note if present — highest-context surface, render first */}
      {panel.clinician_notes && (
        <div className="mb-6">
          <Card title="Note from your clinician" tone="accent">
            <p className="text-[14px] text-fh-ink leading-relaxed m-0 whitespace-pre-wrap">
              {panel.clinician_notes}
            </p>
          </Card>
        </div>
      )}

      {/* Results table */}
      {panel.lab_results.length === 0 ? (
        <Card>
          <p className="text-[14px] text-fh-gray m-0 text-center py-6">
            {panel.status === "ordered" || panel.status === "collected"
              ? "Results haven't come back yet. They typically arrive within 1–3 business days after collection."
              : "No individual results recorded for this panel."}
          </p>
        </Card>
      ) : (
        <Card title="Results">
          <div>
            {panel.lab_results.map((r) => <LabResultRow key={r.id} result={r} />)}
          </div>
        </Card>
      )}

      {/* Footer disclaimer */}
      <p className="text-center text-[11.5px] text-fh-gray mt-6 max-w-[600px] mx-auto leading-relaxed">
        Reference ranges shown are the lab's standard population ranges. Values outside the range are not always concerning — your clinician's interpretation considers your age, sex, medication, and overall context. Reach out via Messages if you have questions about any result.
      </p>
    </AppLayout>
  );
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}
